import React, { FC, HTMLAttributes, ReactNode, useEffect, useId, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { TIcons } from '../../../types/icons.type';
import Icon, { IIconProps } from '../../icon/Icon';
import useAsideStatus from '../../../hooks/useAsideStatus';
import themeConfig from '../../../config/theme.config';
import Tooltip from '../../ui/Tooltip';
import Avatar from '../../Avatar';
import { TColors } from '../../../types/colors.type';
import { useApiReload } from '../../../context/apiRelaod';

const navItemClasses = {
	default: classNames(
		'mb-2 p-3',
		'flex items-center',
		'cursor-pointer',
		'overflow-hidden',
		'rounded-xl',
		'border',
		'text-zinc-500',
		'hover:text-zinc-950 dark:hover:text-black',
		'grow',
		themeConfig.transition,
	),
	inactive: 'secondary-bg border-transparent',
	active: 'border-zinc-300 bg-blue-300 text-zinc-950 dark:border-zinc-800 dark:text-black',
	here: 'text-zinc-950 dark:text-zinc-100 border-transparent',
};
const navItemSidetopClasses = {
	default: classNames(
		'mb-2 p-3',
		'flex w-full',
		'cursor-pointer',
		'overflow-hidden',
		// 'rounded-xl',
		'border-b',
		
		'text-white',
		'hover:text-white ',
		'grow',
		themeConfig.transition,
	),
	
	inactive: ' border-transparent',
	active: 'border-zinc-300 text-white ',
	here: 'text-white  border-transparent',
};

const navItemChildCheck = (
	children:
		| React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>
		| string
		| number
		| Iterable<React.ReactNode>
		| React.ReactPortal
		| boolean
		| null
		| undefined
		| INavButtonProps,
): boolean => {
	// @ts-expect-error
	return children?.length > 1
		? // @ts-expect-error
			children?.map((child) => child.type.displayName).includes('NavButton')
		: // @ts-expect-error
			children?.type?.displayName === 'NavButton';
};

interface INavItemTextProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	className?: string;
}
const NavItemText: FC<INavItemTextProps> = (props) => {
	const { children, className, ...rest } = props;

	return (
		<div
			data-component-name='Nav/NavItemText'
			className={classNames('text-white overflow-hidden truncate whitespace-nowrap', className)}
			{...rest}>
			{children}
		</div>
	);
};
NavItemText.displayName = 'NavItemText';

interface INavItemTextProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	className?: string;
}
const NavItemTextList: FC<INavItemTextProps> = (props) => {
	const { children, className, ...rest} = props;
	

	return (
		<div
			data-component-name='Nav/NavItemText'
			className={classNames('overflow-hidden truncate whitespace-nowrap', className)}
			{...rest}>


				<div className=''>{children}</div>
				
		

		</div>	
	);
};
NavItemText.displayName = 'NavItemTextList';

interface INavItemContentProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	className?: string;
}
export const NavItemContent: FC<INavItemContentProps> = (props) => {
	const { children, className, ...rest } = props;

	const { asideStatus } = useAsideStatus();

	return (
		<div
			data-component-name='Nav/NavItemContent'
			className={classNames(
				' w-full  truncate',
				{
					hidden: !asideStatus,
				},
				className,
			)}
			{...rest}>
			{children}
		</div>
	);
};
NavItemContent.displayName = 'NavItemContent';

interface INavIconProps extends Partial<IIconProps> {
	icon?: TIcons;
	className?: string;
}
const NavIcon: FC<INavIconProps> = (props) => {
	const { className, icon = 'HeroMinus' } = props;

	const { asideStatus } = useAsideStatus();

	return (
		<Icon
			data-component-name='Nav/NavIcon'
			icon={icon}
			className={classNames(
				'text-white w-6 flex-none text-2xl',
				{
					'me-3': asideStatus,
				},
				className,
			)}
		/>
	);
};
NavIcon.displayName = 'NavIcon';

interface INavButtonProps extends HTMLAttributes<HTMLButtonElement> {
	className?: string;
	icon: TIcons;
	iconColor?: TColors;
	iconClassName?: string;
	title: string;
}
export const NavButton: FC<INavButtonProps> = (props) => {
	const { icon, iconColor, className, iconClassName, ...rest } = props;

	return (
		<button
			data-component-name='Nav/NavButton'
			type='button'
			className={classNames(className)}
			{...rest}>
			<Icon
				icon={icon}
				color={iconColor}
				size='text-2xl'
				className={classNames(
					{
						'text-zinc-500 hover:text-zinc-950 dark:hover:text-zinc-100': !iconColor,
					},
					themeConfig.transition,
					iconClassName,
				)}
			/>
		</button>
	);
};
NavButton.displayName = 'NavButton';

interface INavItemProps extends HTMLAttributes<HTMLLIElement> {
	children?: ReactNode;
	icon?: TIcons;
	text: string;
	to?: string;
	className?: string;
	email?:string;
	firstName?:string;
	lastName?:string;
	patientId?:string;
	plan?:boolean;

}
export const NavItem: FC<INavItemProps> = (props) => {
	const { children, icon, text, to,plan ,className, ...rest } = props;

	const { t } = useTranslation('menu');

	const { asideStatus, setAsideStatus } = useAsideStatus();

	const isChildrenNavButton = navItemChildCheck(children);

	const CONTENT = (
		<>
			<NavIcon icon={icon} />
			<NavItemContent >
				<NavItemText >{t(text)}</NavItemText>
				{children && !isChildrenNavButton && <div>{children as ReactNode}</div>}
			</NavItemContent>
		</>
	);

	return (
		<Tooltip text={asideStatus ? '' : t(text)} placement='right'>
			<li
				data-component-name='Nav/NavItem'
				className={classNames(
					'flex list-none items-center overflow-hidden whitespace-nowrap',
					className,
				)}
				{...rest}>
				{to ? (
					<>
						{/* For Desktop */}
						<NavLink
							end
							to={to}
							className={({ isActive }) =>
								isActive
									? classNames(
											'primary-bg',
											navItemClasses.default,
											navItemClasses.active,
											'max-md:hidden',
										)
									: classNames(
											'secondary-bg',
											navItemClasses.default,
											navItemClasses.inactive,
											'max-md:hidden',
										)
							}>
							{CONTENT}
						</NavLink>
						{/* For Mobile */}
						<NavLink
							end
							to={to}
							onClick={() => setAsideStatus(false)}
							className={({ isActive }) =>
								isActive
									? classNames(
										'primary-bg',
											navItemClasses.default,
											navItemClasses.active,
											'md:hidden',
										)
									: classNames(
										'secondary-bg',
											navItemClasses.default,
											navItemClasses.inactive,
											'md:hidden',
										)
							}>
							{CONTENT}
						</NavLink>
					</>
				) : (
					<>
						{/* For Desktop */}
						<div
							className={classNames(
								navItemClasses.default,
								navItemClasses.inactive,
								'max-md:hidden',
							)}>
							{CONTENT}
						</div>
						{/* For Mobile */}
						<div
							className={classNames(
								'secondary-bg',
								navItemClasses.default,
								navItemClasses.inactive,
								'md:hidden',
							)}>
							{CONTENT}
						</div>
					</>
				)}
				{asideStatus && children && isChildrenNavButton && (
					<div className='mb-2 flex items-center gap-3 px-3'>{children as ReactNode}</div>
				)}
			</li>
		</Tooltip>
	);
};
NavItem.displayName = 'NavItem';



export const NavTopItem: FC<INavItemProps> = (props) => {
	const{setPatientID}=useApiReload();
	const { children, firstName, lastName, className, email, id,...rest } = props;

	const { t } = useTranslation('menu');

	const { asideStatus, setAsideStatus } = useAsideStatus();
	const isChildrenNavButton = navItemChildCheck(children);

	const CONTENT = (
		<>
			<div   onClick={() => {
    setPatientID({
      firstName: firstName!,
      lastName: lastName!,
      email: email!,
      id: id!,
	  moblieNo:"",
    });
  }}>
			<NavItemContent className='hover:text-zinc-400 hover:scale-95 ' >
				<NavItemTextList className='capitalize  text-zinc-950' >{firstName!+" "  + lastName! }</NavItemTextList>
				<NavItemText  className='font-normal text-zinc-500'>{email}</NavItemText>
				{children && !isChildrenNavButton && <div>{children as ReactNode}</div>}
			</NavItemContent>
			<NavIcon icon="HeroChevronRight" className='text-zinc-950' />
			</div>
		</>
	);

	return (
		<Tooltip text={asideStatus ? '' : email?"":""} placement='right'>
			<li
				data-component-name='Nav/NavItem'
				className={classNames(
					'flex list-none items-center overflow-hidden whitespace-nowrap',
					className,
				)}
				{...rest}>
				{/* {to ? (
					<>
						{/* For Desktop */}
						{/* <NavLink
							end
							to={to}
							className={({ isActive }) =>
								isActive
									? classNames(
											navItemSidetopClasses.default,
											navItemSidetopClasses.active,
											'max-md:hidden',
										)
									: classNames(
											navItemSidetopClasses.default,
											navItemSidetopClasses.inactive,
											'max-md:hidden',
										)
							}>
							{CONTENT}
						</NavLink>
						{/* For Mobile */}
						{/* <NavLink
							end
							to={to}
							onClick={() => setAsideStatus(false)}
							className={({ isActive }) =>
								isActive
									? classNames(
											navItemSidetopClasses.default,
											navItemSidetopClasses.active,

											'md:hidden',
										)
									: classNames(
											navItemSidetopClasses.default,
											navItemSidetopClasses.inactive,
											'md:hidden',
										)
							}>
							{CONTENT}
						</NavLink>
					</> */}
				{/* ) : ( */}
					<>
						{/* For Desktop */}
						<div
							className={classNames(
								navItemSidetopClasses.default,
								navItemSidetopClasses.inactive,
								'max-md:hidden',
							)}>
							{CONTENT}
						</div>
						{/* For Mobile */}
						<div
							className={classNames(
								navItemSidetopClasses.default,
								navItemSidetopClasses.inactive,
								'md:hidden',
							)}>
							{CONTENT}
						</div>
					</>
				{/* )}  */}
				{asideStatus && children && isChildrenNavButton && (
					<div className='mb-2 flex items-center gap-3 px-3'>{children as ReactNode}</div>
				)}
			</li>
		</Tooltip>
	);
};
NavItem.displayName = 'NavTopItem';


interface INavCollapseProps extends HTMLAttributes<HTMLLIElement> {
	children: ReactNode;
	icon?: TIcons;
	text: string;
	to: string;
	className?: string;
}
export const NavCollapse: FC<INavCollapseProps> = (props) => {
	const { children, icon, text, className, to, ...rest } = props;

	const { t } = useTranslation('menu');

	const id = useId();
	const [isActive, setIsActive] = useState<boolean>(false);

	const { asideStatus } = useAsideStatus();

	const location = useLocation();
	const here = to !== '/' && location.pathname.includes(to);

	useEffect(() => {
		setIsActive(here);
	}, [here, location.pathname]);

	return (
		<li
			data-component-name='Nav/NavCollapse'
			className={classNames('list-none overflow-hidden', className)}
			{...rest}>
			<Tooltip text={asideStatus ? '' : t(text)} placement='right'>
				<div
					role='presentation'
					className={
						isActive || here
							? classNames(navItemClasses.default, navItemClasses.here)
							: classNames(navItemClasses.default, navItemClasses.inactive)
					}
					onClick={() => setIsActive(!isActive)}>
					<NavIcon icon={icon} />

					<NavItemContent>
						<NavItemText>{t(text)}</NavItemText>
						<div>
							<Icon
								icon='HeroChevronDown'
								className={classNames(
									'text-2xl',
									{
										'rotate-180': isActive,
									},
									themeConfig.transition,
								)}
							/>
						</div>
					</NavItemContent>
				</div>
			</Tooltip>
			<AnimatePresence>
				{isActive && (
					<motion.ul
						key={id}
						initial='collapsed'
						animate='open'
						exit='collapsed'
						variants={{
							open: { height: 'auto' },
							collapsed: { height: 0 },
						}}
						transition={{ duration: 0.3 }}
						className={classNames('!transition-margin !duration-300 !ease-in-out', {
							'ms-4': asideStatus,
						})}>
						{children}
					</motion.ul>
				)}
			</AnimatePresence>
		</li>
	);
};
NavCollapse.displayName = 'NavCollapse';

interface INavTitleProps extends HTMLAttributes<HTMLLIElement> {
	children: string;
	className?: string;
}
export const NavTitle: FC<INavTitleProps> = (props) => {
	const { children, className, ...rest } = props;

	const { t } = useTranslation('menu');

	const { asideStatus } = useAsideStatus();

	return (
		<Tooltip text={asideStatus ? '' : t(children)} placement='right'>
			<li
				data-component-name='Nav/NavTitle'
				className={classNames(
					'list-none overflow-hidden truncate whitespace-nowrap px-3 py-1.5 text-sm font-semibold text-zinc-500',
					className,
				)}
				{...rest}>
				{asideStatus ? (
					children
				) : (
					<div className='my-1.5 h-2 w-full max-w-[6rem] rounded-full bg-zinc-500' />
				)}
			</li>
		</Tooltip>
	);
};
NavTitle.displayName = 'NavTitle';

interface INavUserProps extends HTMLAttributes<HTMLLIElement> {
	children?: ReactNode;
	image?: string;
	text: string;
	to?: string;
	className?: string;
}
export const NavUser: FC<INavUserProps> = (props) => {
	const { children, image, text, to, className, ...rest } = props;

	const { t } = useTranslation('menu');

	const { asideStatus, setAsideStatus } = useAsideStatus();

	const isChildrenNavButton = navItemChildCheck(children);

	const CONTENT = (
		<>
			<Avatar
				src={image}
				name={text}
				className={classNames('w-6 rounded-full', {
					'me-3': asideStatus,
				})}
				rounded='rounded'
			/>
			<NavItemContent>
				<NavItemText>{t(text)}</NavItemText>
				{children && !isChildrenNavButton && <div>{children as ReactNode}</div>}
			</NavItemContent>
		</>
	);

	return (
		<Tooltip text={asideStatus ? '' : t(text)} placement='right'>
			<li
				data-component-name='Nav/NavUser'
				className={classNames(
					'flex list-none items-center overflow-hidden whitespace-nowrap',
					className,
				)}
				{...rest}>
				{to ? (
					<>
						{/* For Desktop */}
						<NavLink
							end
							to={to}
							className={({ isActive }) =>
								isActive
									? classNames(
											navItemClasses.default,
											navItemClasses.active,
											'max-md:hidden',
										)
									: classNames(
											navItemClasses.default,
											navItemClasses.inactive,
											'max-md:hidden',
										)
							}>
							{CONTENT}
						</NavLink>
						{/* For Mobile */}
						<NavLink
							end
							to={to}
							onClick={() => setAsideStatus(false)}
							className={({ isActive }) =>
								isActive
									? classNames(
											navItemClasses.default,
											navItemClasses.active,
											'md:hidden',
										)
									: classNames(
											navItemClasses.default,
											navItemClasses.inactive,
											'md:hidden',
										)
							}>
							{CONTENT}
						</NavLink>
					</>
				) : (
					<>
						{/* For Desktop */}
						<div
							className={classNames(
								navItemClasses.default,
								navItemClasses.inactive,
								'max-md:hidden',
							)}>
							{CONTENT}
						</div>
						{/* For Mobile */}
						<div
							className={classNames(
								navItemClasses.default,
								navItemClasses.inactive,
								'md:hidden',
							)}>
							{CONTENT}
						</div>
					</>
				)}
				{asideStatus && children && isChildrenNavButton && (
					<div className='mb-2 flex items-center gap-3 px-3'>{children as ReactNode}</div>
				)}
			</li>
		</Tooltip>
	);
};
NavUser.displayName = 'NavUser';

interface INavSeparatorProps extends HTMLAttributes<HTMLLIElement> {
	className?: string;
}
export const NavSeparator: FC<INavSeparatorProps> = (props) => {
	const { className, ...rest } = props;
	return (
		<li
			data-component-name='Nav/NavSeparator'
			className={classNames(
				'mb-2 list-none rounded-full border-b border-zinc-500/25 mt-3',
				className,
			)}
			{...rest}
		/>
	);
};
NavSeparator.displayName = 'NavSeparator';

interface INavProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	className?: string;
}
const Nav: FC<INavProps> = (props) => {
	const { children, className, ...rest } = props;

	return (
		<nav data-component-name='Nav' className={classNames(className)} {...rest}>
			<ul>{children}</ul>
		</nav>
	);
};
Nav.displayName = 'Nav';

export default Nav;
